/**
 * Passwordless authentication endpoints (one-time code + Google Sign-In).
 *
 * These deliberately do NOT go through `service/axios`. That instance carries a
 * response interceptor which treats 401 as "session expired" (logout + bounce
 * to the home page) and 403 as "forbidden" (bounce to the home page). Both are
 * correct for an authenticated API call and wrong here: on the login screen a
 * 401 means "that code was not right, try again" and must leave the visitor
 * exactly where they are. So this is a bare client, same base URL, no
 * interceptors, no Authorization header - none of these calls have a session to
 * authenticate with yet.
 *
 * Shapes follow the split login/signup auth contract: /otp/request and
 * /otp/verify both take an explicit `purpose`, and the server answers
 * differently for each.
 */

import axios, { AxiosError } from 'axios';

import service from './constant';

export const authClient = axios.create({
    baseURL: service.API_URL + '/api/v1/client',
    headers: {
        'Content-Type': 'application/json'
    }
});

/**
 * Which flow the visitor is in. This is not cosmetic - the server uses it to
 * decide whether an unknown address is an error (login) or the whole point
 * (signup), and whether a code redeems into a session or a registration token.
 */
export type OtpPurpose = 'login' | 'signup';

/**
 * How the code is delivered. `email` always works. `whatsapp` works for SIGN-IN
 * only, and only for a number the customer has verified -- signup stays
 * email-first. Whether it is switched on at all is the server's call: ask
 * `fetchOtpChannels`, and still cope with `isChannelUnavailableError` (501) in
 * case it was switched off since. `sms` is out of scope and always 501.
 */
export type OtpChannel = 'email' | 'whatsapp' | 'sms';

/**
 * The payload every successful sign-in returns, whatever the channel. Matches
 * `respondWithSession` in the backend auth-otp controller, so `handleLogin`
 * consumes all of them unchanged.
 */
export interface AuthUserData {
    userName: string;
    email: string;
    customer: any;
    hasPlacedAnyOrder: boolean;
    hasAnyCard: boolean;
    /** Whether this customer's mobile is verified, i.e. whether WhatsApp sign-in works for them. */
    mobileVerified?: boolean;
    /** Whether the account email is proven (email code, Google or signup). A verified email or mobile can't be changed. */
    emailVerified?: boolean;
    /**
     * Show the one-time "Verify your mobile number" pop-up now. The server
     * decides: WhatsApp is on, the account mobile is an Indian number that is
     * not verified, and the pop-up has not been shown on the website or the
     * app yet.
     */
    mobileVerifyPrompt?: boolean;
}

export interface AuthSessionResponse {
    message: string;
    accessToken: string;
    userData: AuthUserData;
    /**
     * Where the server wants the client to go next. `card_activation` comes
     * back from /otp/complete-registration and means "send them to the QR
     * flow rather than the home page".
     */
    nextStep?: string;
}

/**
 * The only `nextStep` the server emits today.
 *
 * Kept as documentation of the response shape, deliberately unused: card
 * activation is app-only, so the website ignores this field rather than routing
 * on it. The app is what acts on it.
 */
export const NEXT_STEP_CARD_ACTIVATION = 'card_activation';

export interface OtpRequestPayload {
    /** An email address, or a mobile number once whatsapp/sms exist. */
    destination: string;
    purpose: OtpPurpose;
    channel?: OtpChannel;
}

export interface OtpRequestResponse {
    message: string;
    expiresInMinutes?: number;
}

export interface OtpVerifyPayload {
    destination: string;
    code: string;
    purpose: OtpPurpose;
    channel?: OtpChannel;
    rememberMe?: boolean;
}

/**
 * /otp/verify with `purpose: "signup"`: no session yet, just a short-lived
 * token to spend on the profile step.
 */
export interface OtpRegistrationResponse {
    message: string;
    registrationToken: string;
    expiresInMinutes?: number;
}

export type OtpVerifyResponse = AuthSessionResponse | OtpRegistrationResponse;

/**
 * A signup code redeems into a registration token, a login code into a session.
 * The token is what distinguishes them, so test for it rather than trusting the
 * purpose we sent - a server that answers with a session during signup (an
 * address that quietly gained an account) is then still handled.
 */
export const isRegistrationResponse = (data: OtpVerifyResponse | undefined): data is OtpRegistrationResponse =>
    Boolean(data && typeof (data as OtpRegistrationResponse).registrationToken === 'string' && (data as OtpRegistrationResponse).registrationToken);

export interface CompleteRegistrationPayload {
    registrationToken: string;
    firstName: string;
    lastName: string;
    /** Both contact fields are required: one was verified, the other is collected here. */
    email: string;
    mobile: string;
    /**
     * The dial code picked beside the mobile, as the country list has it ("+91").
     * The server stores the bare calling code ("91") and reads an absent one as India.
     */
    countryCode?: string;
    /**
     * Proof the mobile was verified by WhatsApp on this same signup screen.
     * Optional -- verification can be skipped -- but if sent it must match
     * `mobile`, or the server refuses with `mobileVerificationInvalid`.
     */
    mobileVerificationToken?: string;
}

/**
 * Ask for a code.
 *
 * Unlike the old shared endpoint, this one is explicit about whether the
 * address is known: `login` against an unknown address is a 404, and `signup`
 * against a known one is a 409. That is a deliberate trade - distinct flows
 * cost the account-enumeration protection the single endpoint had - so the
 * per-IP and per-destination rate limits on the server are load bearing.
 */
export const requestOtp = async ({ destination, purpose, channel = 'email' }: OtpRequestPayload): Promise<OtpRequestResponse> => {
    const response = await authClient.post<OtpRequestResponse>('/otp/request', {
        destination: destination.trim(),
        channel: channel,
        purpose: purpose
    });
    return response.data;
};

/** Redeem a code: a session for `login`, a registrationToken for `signup`. */
export const verifyOtp = async ({ destination, code, purpose, channel = 'email', rememberMe = true }: OtpVerifyPayload): Promise<OtpVerifyResponse> => {
    const response = await authClient.post<OtpVerifyResponse>('/otp/verify', {
        destination: destination.trim(),
        code: code.trim(),
        purpose: purpose,
        channel: channel,
        rememberMe: rememberMe
    });
    return response.data;
};

/** Spend a registrationToken: creates the account and signs the person in. */
export const completeOtpRegistration = async (payload: CompleteRegistrationPayload): Promise<AuthSessionResponse> => {
    const response = await authClient.post<AuthSessionResponse>('/otp/complete-registration', {
        registrationToken: payload.registrationToken,
        firstName: payload.firstName.trim(),
        lastName: payload.lastName.trim(),
        email: payload.email.trim(),
        mobile: payload.mobile.trim(),
        ...(payload.countryCode ? { countryCode: payload.countryCode } : {}),
        ...(payload.mobileVerificationToken ? { mobileVerificationToken: payload.mobileVerificationToken } : {})
    });
    return response.data;
};

// --- Which channels are on, known before the login form draws ----------------
//
// The login form used to ask when it mounted and start on "email only" until
// the answer came back, so the Email | WhatsApp tabs popped in a moment later
// and pushed the form down. Now the answer is fetched as the site loads
// (index.tsx), remembered in this browser for the next visit, and while it is
// still unknown the form shows a short loader instead. The server stays the
// authority: every fetch replaces the remembered value.

const CHANNELS_STORAGE_KEY = 'otpChannels';
const CHANNELS_TIMEOUT_MS = 6000;
const KNOWN_CHANNELS: OtpChannel[] = ['email', 'whatsapp', 'sms'];

const parseChannels = (value: unknown): OtpChannel[] | null => {
    if (!Array.isArray(value)) return null;
    const channels = KNOWN_CHANNELS.filter((channel) => value.includes(channel));
    return channels.length ? channels : null;
};

const readRememberedChannels = (): OtpChannel[] | null => {
    try {
        return parseChannels(JSON.parse(localStorage.getItem(CHANNELS_STORAGE_KEY) || 'null'));
    } catch {
        return null;
    }
};

let knownChannels: OtpChannel[] | null = readRememberedChannels();
let channelsInFlight: Promise<OtpChannel[]> | null = null;

/** The channels known right now - remembered or already answered - or null. */
export const getKnownOtpChannels = (): OtpChannel[] | null => knownChannels;

/**
 * Which channels the server has switched on. Never throws, and concurrent
 * callers share one request. A failure or timeout keeps the remembered answer,
 * or - with none - email only, which is always true and never offers something
 * that then fails.
 */
export const fetchOtpChannels = (): Promise<OtpChannel[]> => {
    if (channelsInFlight) return channelsInFlight;
    channelsInFlight = (async () => {
        try {
            const { data } = await authClient.get<{ channels?: OtpChannel[] }>('/otp/channels', { timeout: CHANNELS_TIMEOUT_MS });
            const fresh = parseChannels(data?.channels) ?? ['email'];
            knownChannels = fresh;
            try {
                localStorage.setItem(CHANNELS_STORAGE_KEY, JSON.stringify(fresh));
            } catch {
                // Storage blocked (private mode): this visit still has the answer.
            }
        } catch (err) {
            // A backend from before WhatsApp answers 404: email is all there is.
            // Anything else (offline, timeout) keeps what was remembered.
            if (axios.isAxiosError(err) && err.response?.status === 404) knownChannels = ['email'];
            knownChannels = knownChannels ?? ['email'];
        } finally {
            channelsInFlight = null;
        }
        return knownChannels;
    })();
    return channelsInFlight;
};

// ---------------------------------------------------------------------------
// Mobile verification by WhatsApp
// ---------------------------------------------------------------------------

/**
 * Who is verifying: mid-signup (holding a registrationToken) or signed in
 * (holding a session token). One of the two is required server-side.
 *
 * The session token is sent on THIS bare client rather than through
 * `service/axios` on purpose. That instance logs the visitor out on any 401, and
 * a mistyped code is a 401 -- going through it would sign someone out for a
 * typo.
 */
export interface MobileVerificationCaller {
    registrationToken?: string;
    accessToken?: string;
}

const callerConfig = ({ accessToken }: MobileVerificationCaller) =>
    accessToken ? { headers: { Authorization: accessToken } } : undefined;

export interface MobileVerificationResponse {
    message: string;
    /** Signup only: hand this to completeOtpRegistration. */
    mobileVerificationToken?: string;
    /** Profile only: the number is now verified on the account. */
    mobileVerified?: boolean;
    /** Profile only: the number was already verified; nothing was sent. */
    alreadyVerified?: boolean;
    /** Signup only: the step can be skipped. */
    canSkip?: boolean;
}

export const requestMobileVerification = async (mobile: string, caller: MobileVerificationCaller): Promise<MobileVerificationResponse> => {
    const response = await authClient.post<MobileVerificationResponse>(
        '/otp/mobile/request',
        { mobile: mobile.trim(), ...(caller.registrationToken ? { registrationToken: caller.registrationToken } : {}) },
        callerConfig(caller)
    );
    return response.data;
};

export const verifyMobile = async (mobile: string, code: string, caller: MobileVerificationCaller): Promise<MobileVerificationResponse> => {
    const response = await authClient.post<MobileVerificationResponse>(
        '/otp/mobile/verify',
        { mobile: mobile.trim(), code: code.trim(), ...(caller.registrationToken ? { registrationToken: caller.registrationToken } : {}) },
        callerConfig(caller)
    );
    return response.data;
};

/**
 * Record that the "Verify your mobile number" pop-up was shown, so neither the
 * website nor the app shows it again. On the bare client for the same reason
 * as above: a failure here must never sign anyone out.
 */
export const markMobileVerifyPromptShown = async (accessToken: string): Promise<void> => {
    await authClient.post('/otp/mobile/prompt-shown', {}, { headers: { Authorization: accessToken } });
};

/** Trade a Google ID token for a session. The identity comes out of the token, never the body. */
export const googleSignIn = async (idToken: string, rememberMe = true): Promise<AuthSessionResponse> => {
    const response = await authClient.post<AuthSessionResponse>('/auth/google', {
        idToken: idToken,
        rememberMe: rememberMe
    });
    return response.data;
};

// ---------------------------------------------------------------------------
// Error helpers
// ---------------------------------------------------------------------------

interface AuthErrorBody {
    message?: string;
    retryAfterSeconds?: number;
    passwordLoginDisabled?: boolean;
    /** false on a 404 from a login request, true on a 409 from a signup one. */
    accountExists?: boolean;
    /** 'email' when a WhatsApp attempt failed and email is the way forward. */
    fallbackChannel?: OtpChannel;
    /** 409 at mobile verification: the number is verified on another account. */
    mobileTaken?: boolean;
    /** Signup mobile verification: this step may be skipped. */
    canSkip?: boolean;
    /** 400 at complete-registration: the WhatsApp proof did not hold up. */
    mobileVerificationInvalid?: boolean;
    errors?: { msg?: string }[];
    /** Some of the older client routes nest the same list one level down. */
    fields?: { errors?: { msg?: string }[] };
}

const errorBody = (error: unknown): AuthErrorBody | undefined => (error as AxiosError<AuthErrorBody>)?.response?.data;

/**
 * Prefer whatever the server said. Its messages are written for the visitor and
 * are careful about what they do and do not reveal, so paraphrasing them
 * locally risks both a worse message and an accidental leak.
 */
export const authErrorMessage = (error: unknown, fallback = 'Something went wrong. Please try again.'): string => {
    const body = errorBody(error);
    if (body?.message) return body.message;

    // express-validator failures come back as a list rather than a message, and
    // the client routes are not consistent about whether it sits at the top
    // level or under `fields`. Look in both before giving up.
    const validationErrors = body?.errors ?? body?.fields?.errors;
    const firstValidationError = validationErrors?.find((item) => item?.msg)?.msg;
    if (firstValidationError) return firstValidationError;

    if ((error as AxiosError)?.code === 'ERR_NETWORK') {
        return 'We could not reach the server. Please check your connection and try again.';
    }

    return fallback;
};

export const authErrorStatus = (error: unknown): number | undefined => (error as AxiosError)?.response?.status;

/** 429 from /otp/request: the address is throttled. Never retry automatically. */
export const isThrottledError = (error: unknown): boolean => authErrorStatus(error) === 429;

/** How long the server says to wait, when it bothers to say. */
export const throttleRetryAfterSeconds = (error: unknown): number | undefined => {
    const seconds = errorBody(error)?.retryAfterSeconds;
    return typeof seconds === 'number' && seconds > 0 ? Math.ceil(seconds) : undefined;
};

/**
 * 404 `{ accountExists: false }` - a login request for an address with no
 * account. The `accountExists` flag is what separates this from a plain 404 off
 * a misrouted URL, so both are required before we tell somebody their account
 * does not exist.
 */
export const isAccountMissingError = (error: unknown): boolean =>
    authErrorStatus(error) === 404 && errorBody(error)?.accountExists === false;

/**
 * 409 `{ accountExists: true }` - a signup request for an address that already
 * has an account, either at /otp/request or at /otp/complete-registration when
 * the *other* contact field turns out to be taken.
 */
export const isAccountExistsError = (error: unknown): boolean =>
    authErrorStatus(error) === 409 && errorBody(error)?.accountExists === true;

/**
 * 501 from /otp/request: the chosen channel is not wired up yet. Only reachable
 * with channel whatsapp/sms, which no screen sends today - this is here so that
 * the day one does, it degrades into the server message instead of a generic
 * failure.
 */
export const isChannelUnavailableError = (error: unknown): boolean => authErrorStatus(error) === 501;

/**
 * The server pointed at email as the way forward: an unverified or unknown
 * WhatsApp number, a WhatsApp send that failed, or the per-number limit. Whatever
 * the cause, the visitor's next move is the same, so callers branch on this
 * rather than on the status code.
 */
export const isEmailFallbackError = (error: unknown): boolean => errorBody(error)?.fallbackChannel === 'email';

/** 409 `{ mobileTaken: true }` - the number is verified on a different account. */
export const isMobileTakenError = (error: unknown): boolean => authErrorStatus(error) === 409 && Boolean(errorBody(error)?.mobileTaken);

/** 400 `{ mobileVerificationInvalid: true }` - the signup's WhatsApp proof expired or no longer matches. */
export const isMobileVerificationInvalidError = (error: unknown): boolean =>
    authErrorStatus(error) === 400 && Boolean(errorBody(error)?.mobileVerificationInvalid);

/** 403 `{ passwordLoginDisabled: true }` - password sign-in has been switched off server-side. */
export const isPasswordLoginDisabledError = (error: unknown): boolean =>
    authErrorStatus(error) === 403 && Boolean(errorBody(error)?.passwordLoginDisabled);

// ---------------------------------------------------------------------------
// Dial codes
// ---------------------------------------------------------------------------

/** One row of `country-code`, named as the API returns it (snake_case). */
export interface CountryCode {
    id: number;
    name: string;
    iso_code: string;
    dial_code: string;
}

/** What the picker falls back to when the list cannot be fetched. */
export const DEFAULT_DIAL_CODE = '+91';

/**
 * The dial-code list behind the mobile field on signup.
 *
 * Deliberately points at `/api/m1/`, not the `/api/v1/` base every other call in
 * this file uses: `country-code` is registered only under the mobile prefix
 * (mobile_apis/index.js), so a `/api/v1/client/country-code` request is a 404.
 * It is public - `clientAuth` is commented out on that route - and returns the
 * same rows for either client, so the web reads the mobile path rather than
 * making this website change depend on a backend route shipping first.
 *
 * Never throws: the caller renders a working field with the default dial code
 * if this comes back empty, because a failed lookup of a cosmetic list must not
 * be able to block a signup.
 */
export const fetchCountryCodes = async (): Promise<CountryCode[]> => {
    try {
        const { data } = await authClient.get('/country-code', {
            baseURL: service.API_URL + '/api/m1/client'
        });
        const rows: CountryCode[] = Array.isArray(data) ? data : data?.data ?? [];
        return rows.filter((row) => row && row.dial_code);
    } catch {
        return [];
    }
};
